import React, { useEffect, useState } from "react";
import "../../assets/css/Urls.css";
import { createUrl, updateUrl, deleteUrl, listUrls, listBlockRules, createBlockRule, deleteBlockRule } from "../../services/urlService";
import { getProjectPid } from "../../utils/sessionProject";
import { toast } from 'react-toastify';


const createScanRow = () => ({ id: crypto.randomUUID(), url: "", urlPid: null });
const createBlockRow = () => ({ id: crypto.randomUUID(), pattern: "", type: "wildcard", blockPid: null });

const UrlSettings = () => {
  const [scanRows, setScanRows] = useState([createScanRow()]);
  const [blockRows, setBlockRows] = useState([createBlockRow()]);
  const [scanSaving, setScanSaving] = useState(false);
  const [blockSaving, setBlockSaving] = useState(false);

  const projectPid = getProjectPid();

  useEffect(() => {
    const loadScanUrls = async () => {
      try {
        const res = await listUrls({ projectPid, page: 1, limit: 100, isAddedByUser: 1 });
        const items = res?.response?.items ?? res?.response?.urls ?? [];
        if (items.length) {
          setScanRows(items.map((u) => ({ id: crypto.randomUUID(), url: u.url, urlPid: u.urlPid })));
        }
      } catch (err) {
        console.error('Failed to load scan URLs', err);
      }
    };

    const loadBlockRules = async () => {
      try {
        const res = await listBlockRules({ projectPid });
        const items = res?.response?.items ?? res?.response?.blocks ?? res?.response ?? [];
        if (Array.isArray(items) && items.length) {
          setBlockRows(items.map((b) => ({ id: crypto.randomUUID(), pattern: b.pattern || b.url || "", type: b.blocktype || "wildcard", blockPid: b.blockPid })));
        }
      } catch (err) {
        console.error('Failed to load block rules', err);
      }
    };

    loadScanUrls();
    loadBlockRules();
  }, [projectPid]);

  const updateScanRow = (id, value) =>
    setScanRows((rows) => rows.map((row) => (row.id === id ? { ...row, url: value } : row)));

  const updateBlockRow = (id, field, value) =>
    setBlockRows((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));

  const deleteScanRow = async (id) => {
    const row = scanRows.find((r) => r.id === id);
    if (row?.urlPid) {
      try { await deleteUrl({ urlPid: row.urlPid }); } catch (err) { console.error('Delete failed', err); return; }
    }
    setScanRows((rows) => {
      const next = rows.filter((r) => r.id !== id);
      return next.length ? next : [createScanRow()];
    });
  };

  const deleteBlockRow = async (id) => {
    const row = blockRows.find((r) => r.id === id);
    if (row?.blockPid) {
      try { await deleteBlockRule({ projectPid, blockPid: row.blockPid }); } catch (err) { console.error('Delete block rule failed', err); return; }
    }
    setBlockRows((rows) => {
      const next = rows.filter((r) => r.id !== id);
      return next.length ? next : [createBlockRow()];
    });
  };

  const handleSaveScanUrls = async () => {
    const rows = scanRows.filter((row) => row.url.trim());
    if (!rows.length) return;
    setScanSaving(true);
    try {
      const results = await Promise.all(
        rows.map((row) =>
          row.urlPid
            ? updateUrl({ urlPid: row.urlPid, projectPid, url: row.url.trim() })
            : createUrl({ projectPid, url: row.url.trim() })
        )
      );
      setScanRows(
        rows.map((row, i) => ({
          ...row,
          urlPid: row.urlPid || results[i]?.response?.urlPid || null,
        }))
      );
      toast.success("Scan URLs saved successfully!");
    } catch (err) {
      console.error('Save scan URLs failed', err);
    } finally {
      setScanSaving(false);
    }
  };

  const handleSaveBlockUrls = async () => {
    const rows = blockRows.filter((row) => row.pattern.trim());
    if (!rows.length) return;
    setBlockSaving(true);
    try {
      const newRows = rows.filter((row) => !row.blockPid);
      await Promise.all(
        newRows.map((row) => createBlockRule({ projectPid, pattern: row.pattern.trim(), blocktype: row.type }))
      );
      await loadBlockRulesIntoState();
      toast.success("Block rules saved successfully!");
    } catch (err) {
      console.error('Save block rules failed', err);
    } finally {
      setBlockSaving(false);
    }
  };

  const loadBlockRulesIntoState = async () => {
    try {
      const res = await listBlockRules({ projectPid });
      const items = res?.response?.items ?? res?.response?.blocks ?? res?.response ?? [];
      if (Array.isArray(items) && items.length) {
        setBlockRows(items.map((b) => ({ id: crypto.randomUUID(), pattern: b.pattern || b.url || "", type: b.blocktype || "wildcard", blockPid: b.blockPid })));
      }
    } catch (err) {
      console.error('Failed to reload block rules', err);
    }
  };

  return (
    <main className="app-wrapper url-settings-page" id="urlPage">
      <div className="container-fluid">
        <div className="row g-3">

          {/* Scan URLs */}
          <div className="col-12 col-md-6">
            <section className="url-settings-card">
              <div className="url-settings-card-header">
                <h6 className="url-settings-title mb-0">Urls to Scan</h6>
                <button className="btn btn-primary url-save-btn" type="button" onClick={handleSaveScanUrls} disabled={scanSaving}>
                  {scanSaving ? "Saving..." : "Save"}
                </button>
              </div>
              <div className="url-settings-card-body">
                <div className="table-responsive">
                  <table className="table url-settings-table mb-0">
                    <tbody>
                      {scanRows.map((row, index) => (
                        <tr key={row.id}>
                          <td>
                            <input
                              type="text"
                              className="form-control scan-input"
                              placeholder="Enter URL"
                              value={row.url}
                              onChange={(e) => updateScanRow(row.id, e.target.value)}
                            />
                          </td>
                          <td className="url-settings-action-cell">
                            {index > 0 && (
                              <button
                                className="btn btn-sm btn-danger url-delete-row"
                                type="button"
                                onClick={() => deleteScanRow(row.id)}
                                aria-label={`Delete scan URL ${index + 1}`}
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <button
                    className="btn btn-outline-primary url-add-btn"
                    type="button"
                    onClick={() => setScanRows((rows) => [...rows, createScanRow()])}
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Block URLs */}
          <div className="col-12 col-md-6">
            <section className="url-settings-card">
              <div className="url-settings-card-header">
                <h6 className="url-settings-title mb-0">Urls &amp; Patterns to Block</h6>
                <button className="btn btn-primary url-save-btn" type="button" onClick={handleSaveBlockUrls} disabled={blockSaving}>
                  {blockSaving ? "Saving..." : "Save"}
                </button>
              </div>
              <div className="url-settings-card-body">
                <div className="table-responsive">
                  <table className="table url-settings-table mb-0">
                    <tbody>
                      {blockRows.map((row, index) => (
                        <tr key={row.id}>
                          <td>
                            <input
                              type="text"
                              className="form-control block-input"
                              placeholder="Enter URL or pattern"
                              value={row.pattern}
                              onChange={(e) => updateBlockRow(row.id, "pattern", e.target.value)}
                            />
                          </td>
                          <td className="url-block-type-cell">
                            <span className="form-select block-type d-flex align-items-center" style={{ pointerEvents: "none", color: "#475569", background: "#f8fafc" }}>Wildcard</span>
                          </td>
                          <td className="url-settings-action-cell">
                            {index > 0 && (
                              <button
                                className="btn btn-sm btn-danger url-delete-row"
                                type="button"
                                onClick={() => deleteBlockRow(row.id)}
                                aria-label={`Delete blocked URL ${index + 1}`}
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <button
                    className="btn btn-outline-primary url-add-btn"
                    type="button"
                    onClick={() => setBlockRows((rows) => [...rows, createBlockRow()])}
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add
                  </button>
                </div>
              </div>
            </section>
          </div>

        </div>
      </div>
    </main>
  );
};

export default UrlSettings;
